import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Check, X, Camera, PlusCircle, Save, ChevronLeft, AlertTriangle, BookOpen } from 'lucide-react';

export default function InterventionReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intervention, setIntervention] = useState<any>(null);
  const [clientTasks, setClientTasks] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  
  // State for the current report being built
  // A map of task ID (or temp ID) to its state
  const [reportLogs, setReportLogs] = useState<Record<string, any>>({});
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add task modal
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskCategory, setNewTaskCategory] = useState('Autre');
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskIsPermanent, setNewTaskIsPermanent] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    if (!supabase || !id) return;
    
    // 1. Fetch intervention
    const { data: interData } = await supabase
      .from('interventions')
      .select('*, prestation:prestations(client_id, custom_name, client:profiles(full_name))')
      .eq('id', id)
      .single();
      
    if (!interData) {
      navigate('/collaborator');
      return;
    }
    setIntervention(interData);

    const clientId = interData.prestation?.client_id;

    // 2. Fetch Client Tasks & Catalog
    const initialLogs: Record<string, any> = {};

    if (clientId) {
      const { data: ctData } = await supabase.from('client_tasks').select('*').eq('client_id', clientId).order('category').order('name');
      if (ctData) {
        setClientTasks(ctData);
        ctData.forEach(t => {
          const key = `${t.category}_${t.name}`;
          initialLogs[key] = { client_task_id: t.id, category: t.category, name: t.name, status: 'non_fait', comment: '', photo_url: null, file: null, is_mandatory: t.is_mandatory };
        });
      }
    }

    // 3. Fetch task catalog for easy adding and merge missing into initialLogs
    const { data: tData } = await supabase.from('task_templates').select('*').order('category').order('name');
    if (tData) {
      setTaskTemplates(tData);
      tData.forEach(t => {
        const key = `${t.category}_${t.name}`;
        if (!initialLogs[key]) {
          initialLogs[key] = { client_task_id: null, category: t.category, name: t.name, status: 'non_fait', comment: '', photo_url: null, file: null, is_mandatory: false };
        }
      });
    }

    setReportLogs(initialLogs);

    setLoading(false);
  };

  const handleStatusChange = (taskId: string, status: 'fait' | 'non_fait' | 'probleme') => {
    setReportLogs(prev => ({ ...prev, [taskId]: { ...prev[taskId], status } }));
  };

  const handleCommentChange = (taskId: string, comment: string) => {
    setReportLogs(prev => ({ ...prev, [taskId]: { ...prev[taskId], comment } }));
  };

  const handlePhotoSelect = (taskId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setReportLogs(prev => ({ ...prev, [taskId]: { ...prev[taskId], file } }));
    }
  };

  const removePhoto = (taskId: string) => {
    setReportLogs(prev => ({ ...prev, [taskId]: { ...prev[taskId], file: null } }));
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !intervention) return;
    const clientId = intervention.prestation?.client_id;
    
    let taskId = `temp_${Date.now()}`;
    let clientTaskId = null;

    if (newTaskIsPermanent && clientId) {
      // Add to client tasks
      const { data } = await supabase.from('client_tasks').insert({
        client_id: clientId,
        category: newTaskCategory,
        name: newTaskName,
        is_mandatory: false
      }).select().single();
      
      if (data) {
        taskId = data.id;
        clientTaskId = data.id;
        setClientTasks([...clientTasks, data]);
      }
    }

    setReportLogs(prev => ({
      ...prev,
      [taskId]: { client_task_id: clientTaskId, category: newTaskCategory, name: newTaskName, status: 'fait', comment: '', photo_url: null, file: null, is_mandatory: false }
    }));

    setShowAddTaskModal(false);
    setNewTaskName('');
    setNewTaskIsPermanent(false);
  };

  const uploadPhoto = async (file: File): Promise<string | null> => {
    if (!supabase) return null;
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${fileName}`;
    const { error: uploadError } = await supabase.storage.from('reports_media').upload(filePath, file);
    if (uploadError) return null;
    const { data } = supabase.storage.from('reports_media').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const submitReport = async () => {
    if (!supabase || !intervention) return;
    
    // Validation: Check if mandatory tasks that are not 'fait' have a comment or photo
    const missingJustifications = Object.values(reportLogs).filter(log => log.is_mandatory && log.status !== 'fait' && !log.comment && !log.file);
    if (missingJustifications.length > 0) {
      alert(`Veuillez justifier (commentaire ou photo) les tâches obligatoires non réalisées :\n${missingJustifications.map(m => m.name).join('\n')}`);
      return;
    }

    setSaving(true);
    try {
      const logsToInsert = [];
      for (const key of Object.keys(reportLogs)) {
        const log = reportLogs[key];
        
        // Skip non_fait non mandatory tasks if user hasn't touched them to save DB space? 
        // Actually, let's log everything to show the client exactly what was checked.
        
        let photo_url = null;
        if (log.file) {
          photo_url = await uploadPhoto(log.file);
        }

        logsToInsert.push({
          intervention_id: intervention.id,
          client_task_id: log.client_task_id,
          category: log.category,
          name: log.name,
          status: log.status,
          comment: log.comment,
          photo_url: photo_url
        });
      }

      const { error: insertError } = await supabase.from('report_task_logs').insert(logsToInsert);
      if (insertError) throw insertError;

      // Mark intervention as done
      const { error: updateError } = await supabase.from('interventions').update({ status: 'termine' }).eq('id', intervention.id);
      if (updateError) throw updateError;

      navigate('/collaborator');
    } catch (err: any) {
      alert("Erreur lors de la sauvegarde : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Chargement...</div>;
  }

  // Group logs by category
  const categories = Array.from(new Set(Object.values(reportLogs).map(l => l.category)));

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-black-deep text-white p-6 sticky top-0 z-10 shadow-lg">
        <button onClick={() => navigate('/collaborator')} className="flex items-center text-gold-400 hover:text-white text-sm uppercase tracking-widest font-bold mb-4">
          <ChevronLeft size={16} className="mr-1" /> Retour
        </button>
        <h1 className="font-serif text-2xl mb-1">Rapport d'Intervention</h1>
        <p className="text-gray-400 text-sm">{intervention.prestation?.client?.full_name} - {intervention.prestation?.custom_name}</p>
      </div>

      <div className="p-4 max-w-3xl mx-auto space-y-8 mt-4">
        
        {categories.length === 0 ? (
          <div className="text-center p-8 bg-white rounded-sm border border-gray-200">
            <p className="text-gray-500 mb-4">Aucune tâche prévue pour ce client.</p>
            <button onClick={() => setShowAddTaskModal(true)} className="px-6 py-3 bg-black-deep text-white font-bold uppercase tracking-widest text-xs rounded-sm">Ajouter une première tâche</button>
          </div>
        ) : (
          categories.map(cat => (
            <div key={cat} className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 p-3 border-b border-gray-200">
                <h2 className="font-bold uppercase tracking-widest text-sm text-gray-800">{cat}</h2>
              </div>
              
              <div className="divide-y divide-gray-100">
                {Object.entries(reportLogs).filter(([_, log]) => log.category === cat).map(([taskId, log]) => (
                  <div key={taskId} className={`p-4 transition-colors ${log.status === 'fait' ? 'bg-green-50/30' : log.status === 'probleme' ? 'bg-red-50/30' : ''}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <p className="font-medium text-black-deep flex items-center gap-2">
                          {log.name} 
                          {log.is_mandatory && <span title="Obligatoire"><AlertTriangle size={14} className="text-red-500" /></span>}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button 
                          onClick={() => handleStatusChange(taskId, 'fait')}
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${log.status === 'fait' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-gray-300 hover:border-green-500 hover:text-green-500'}`}
                        >
                          <Check size={20} />
                        </button>
                        <button 
                          onClick={() => handleStatusChange(taskId, 'probleme')}
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${log.status === 'probleme' ? 'bg-red-500 border-red-500 text-white' : 'border-gray-300 text-gray-300 hover:border-red-500 hover:text-red-500'}`}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Collapsible details if touched or mandatory non-fait */}
                    <div className={`mt-3 space-y-3 ${(log.status !== 'non_fait' || log.is_mandatory) ? 'block' : 'hidden'}`}>
                      <input 
                        type="text" 
                        placeholder={log.status === 'probleme' ? "Décrire le problème rencontré..." : "Commentaire optionnel..."}
                        value={log.comment}
                        onChange={(e) => handleCommentChange(taskId, e.target.value)}
                        className={`w-full text-sm p-2 border rounded-sm focus:outline-none focus:border-gold-500 ${log.status === 'probleme' ? 'bg-white border-red-200' : 'bg-gray-50 border-gray-200'}`}
                      />
                      
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 cursor-pointer hover:text-gold-600 transition-colors">
                          <Camera size={16} />
                          <span>{log.file ? 'Changer Photo' : 'Ajouter Photo'}</span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoSelect(taskId, e)} />
                        </label>
                        {log.file && (
                          <div className="flex items-center gap-2 text-xs font-medium text-black-deep bg-gray-100 px-2 py-1 rounded">
                            <span className="truncate max-w-[150px]">{log.file.name}</span>
                            <button onClick={() => removePhoto(taskId)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        <button onClick={() => setShowAddTaskModal(true)} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-sm text-gray-500 font-bold uppercase tracking-widest text-sm hover:border-gold-500 hover:text-gold-600 transition-colors flex items-center justify-center gap-2">
          <PlusCircle size={18} /> Ajouter une tâche supplémentaire
        </button>
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="max-w-3xl mx-auto flex gap-4">
          <button onClick={submitReport} disabled={saving} className="flex-1 bg-gold-500 text-white py-4 rounded-sm font-bold uppercase tracking-widest text-sm hover:bg-black-deep transition-colors flex items-center justify-center gap-2">
            {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Save size={18} />}
            Valider le rapport
          </button>
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black-deep/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-serif text-lg flex items-center gap-2"><BookOpen className="text-gold-500"/> Ajouter Tâche</h3>
              <button onClick={() => setShowAddTaskModal(false)} className="text-gray-400 hover:text-black-deep"><X size={20}/></button>
            </div>
            <div className="p-6 space-y-6">
              
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Depuis le catalogue</p>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-sm divide-y divide-gray-100">
                  {taskTemplates.map(t => (
                    <button key={t.id} type="button" onClick={() => { setNewTaskCategory(t.category); setNewTaskName(t.name); }} className="w-full text-left p-3 hover:bg-gray-50 text-sm">
                      <span className="font-medium">{t.name}</span> <span className="text-gray-400 text-xs ml-2">({t.category})</span>
                    </button>
                  ))}
                  {taskTemplates.length === 0 && <div className="p-3 text-sm text-gray-500">Catalogue vide</div>}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 space-y-4">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Ou personnalisée</p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Catégorie (Pièce)</label>
                  <input type="text" value={newTaskCategory} onChange={e => setNewTaskCategory(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom de la tâche</label>
                  <input type="text" value={newTaskName} onChange={e => setNewTaskName(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" />
                </div>
                
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                  <input type="checkbox" checked={newTaskIsPermanent} onChange={e => setNewTaskIsPermanent(e.target.checked)} className="text-gold-500 w-4 h-4 rounded-sm border-gray-300" />
                  <span className="text-sm font-medium text-gray-700">Enregistrer cette tâche définitivement pour ce client</span>
                </label>
              </div>

              <button onClick={handleAddTask} disabled={!newTaskName.trim() || !newTaskCategory.trim()} className="w-full bg-black-deep text-white py-3 font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-gold-500 transition-colors disabled:opacity-50">
                Ajouter au rapport
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
